import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { MapPin, ArrowRight } from "lucide-react";
import { catMeta } from "../lib/categories";
import { slugify } from "../lib/slugify";
import { useCityGeo } from "../lib/useCityGeo";

const timeLabel = (iso) => {
  if (!iso || iso.length <= 10) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};
const dayShort = (iso) => {
  if (!iso) return "Date TBC";
  const d = new Date(iso.length <= 10 ? iso + "T00:00" : iso);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

// "Events near you" homepage strip — uses the visitor's detected location to surface
// upcoming events in their nearest city. Renders nothing until a city is detected.
export default function NearbyEvents() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/events/browse").then(({ data }) => setEvents(Array.isArray(data) ? data : [])).catch(() => setEvents([]));
  }, []);

  const cityNames = useMemo(() => events ? [...new Set(events.map((e) => e.city).filter(Boolean))].sort() : [], [events]);
  const { city, nearName } = useCityGeo(cityNames);

  if (!nearName || city !== nearName) return null;
  const nearby = (events || []).filter((e) => (e.city || "") === nearName).slice(0, 8);
  if (nearby.length === 0) return null;

  return (
    <section className="bg-[#0d1626] py-12 border-b border-white/5" data-testid="nearby-events">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-end justify-between mb-5 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-[#2ec9c9] font-semibold flex items-center gap-1.5"><MapPin size={14} /> Events near you</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">What's on in {nearName}</h2>
          </div>
          <Link to={`/city/${slugify(nearName)}`} data-testid="nearby-see-all"
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-[#2ec9c9] hover:text-white transition-colors">
            See all <ArrowRight size={15} />
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
          {nearby.map((e) => {
            const cm = catMeta(e.category); const CIcon = cm.icon;
            return (
              <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`nearby-card-${e.id}`}
                className="group w-64 shrink-0 rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden hover:bg-white/[0.08] hover:border-[#2ec9c9]/40 transition-colors">
                <div className="h-32 bg-white/10 overflow-hidden relative">
                  {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  {e.category && <span className="absolute top-2 right-2 text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1" style={{ background: cm.color + "26", color: cm.color }}><CIcon size={10} /> {e.category}</span>}
                </div>
                <div className="p-3">
                  <p className="text-xs text-[#2ec9c9]">{dayShort(e.event_date)}{timeLabel(e.event_date) ? ` · ${timeLabel(e.event_date)}` : ""}</p>
                  <h3 className="text-white font-semibold truncate mt-1 group-hover:text-[#2ec9c9] transition-colors">{e.title}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#8a97a8] truncate">{e.business_name}</p>
                    <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
